import { useEffect, useMemo, useState } from "react";
import { AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import {
  type Operator,
  type Task,
  TaskStatus,
  type Trip,
  type Waypoint,
} from "@/api.types.v2";
import { taskSearchQueries, useTasks } from "@/api/tasks";
import { useActiveTrips, useAllTripWaypoints } from "@/api/trips";
import { useSocketOperators } from "@/api/operators";
import { useGetDispatchCenters } from "@/api/dispatch-centers";
import {
  tripIsVisible,
  useShortcutsStore,
} from "@/screens/dashboard/shortcuts.store";
import { useDispatchDateStore } from "@/features/dashboard/dispatch-date.store";
import { useCarrierScope } from "@/contexts/carrier-scope-context";
import { useNow } from "@/features/dashboard/use-now";
import {
  applyTaskScopeFilter,
  taskUrgency,
} from "@/features/dashboard/task-urgency";
import { getTaskCareSignals } from "@/features/dispatch/selectors";
import { getLocationCoordinates, getOperatorInitials } from "@/lib/safe-access";
import { emptyLaneCounts, type LaneCounts, taskLane } from "../task-type-meta";
import { buildStops, type DerivedStop } from "../trip-stops";
import { taskIsOnCancellationHold, unresolvedHoldState } from "../work-hold";
import { spreadOverlappingStops } from "./trip-stop-layout";
import {
  DepotMarker,
  OperatorMarker,
  StackedOperators,
  TaskClusterMarker,
  WaypointMarker,
  zoomTier,
} from "./marker-visuals";

const TERMINAL = [
  TaskStatus.COMPLETED,
  TaskStatus.FAILED,
  TaskStatus.CANCELLED,
];

function laneCountsOf(tasks: Task[]): LaneCounts {
  const c = emptyLaneCounts();
  for (const t of tasks) c[taskLane(t.type)] += 1;
  return c;
}

function useScope() {
  const { carrierIds, dispatchCenterIds } = useCarrierScope();
  return {
    carrierIds: carrierIds.join(","),
    dispatchCenterIds: dispatchCenterIds.join(","),
  };
}

// ── unassigned task clusters ────────────────────────────────────────────────
export function TaskClusterMarkers({
  zoomLevel,
  onClickCluster,
}: {
  zoomLevel: number;
  onClickCluster: (tasks: Task[]) => void;
}) {
  const {
    showPickups,
    showDeliveries,
    showCarrierDropoffs,
    showReturns,
    windowFilter,
  } = useShortcutsStore();
  const scope = useScope();
  const selectedDate = useDispatchDateStore((s) => s.selectedDate);
  const now = useNow(60_000);

  const { data: pickups } = useTasks({
    ...taskSearchQueries.activePickupTasks,
    ...scope,
  });
  const { data: deliveries } = useTasks({
    ...taskSearchQueries.activeDeliveryTasks,
    ...scope,
  });
  const { data: dropoffs } = useTasks({
    ...taskSearchQueries.activeCarrierDropoffTasks,
    ...scope,
  });
  const { data: returns } = useTasks({
    ...taskSearchQueries.activeReturnTasks,
    ...scope,
  });

  const tier = zoomTier(zoomLevel);

  const clusters = useMemo(() => {
    const pick = (list: Task[] | undefined, show: boolean) =>
      show
        ? (list ?? []).filter((t) => !t.tripId && !TERMINAL.includes(t.status))
        : [];
    const all = [
      ...pick(pickups?.data, showPickups),
      ...pick(deliveries?.data, showDeliveries),
      ...pick(dropoffs?.data, showCarrierDropoffs),
      ...pick(returns?.data, showReturns),
    ];
    const scoped = applyTaskScopeFilter(all, selectedDate, windowFilter, now);

    const groups = new Map<string, Task[]>();
    for (const t of scoped) {
      const loc = t.address?.location;
      if (loc?.lat == null || loc.lng == null) continue;
      // A held task needs its own visible grey marker even when actionable work
      // shares the same address.
      const key = `${loc.lat},${loc.lng},${taskIsOnCancellationHold(t) ? "held" : "open"}`;
      const arr = groups.get(key) ?? [];
      arr.push(t);
      groups.set(key, arr);
    }
    return [...groups.entries()].map(([key, tasks]) => {
      const [lat, lng, holdKey] = key.split(",");
      let urgency: "red" | "orange" | null = null;
      for (const t of tasks) {
        const u = taskUrgency(t, now);
        if (u === "red") {
          urgency = "red";
          break;
        }
        if (u === "orange") urgency = "orange";
      }
      if (holdKey === "held") urgency = null;
      const note = tasks.some((t) => getTaskCareSignals(t).note);
      const fragile = tasks.some((t) => getTaskCareSignals(t).fragile);
      return {
        key,
        position: { lat: Number(lat), lng: Number(lng) },
        tasks,
        urgency,
        note,
        fragile,
        held: holdKey === "held",
      };
    });
  }, [
    pickups,
    deliveries,
    dropoffs,
    returns,
    showPickups,
    showDeliveries,
    showCarrierDropoffs,
    showReturns,
    selectedDate,
    windowFilter,
    now,
  ]);

  return (
    <>
      {clusters.map((c) => (
        <AdvancedMarker key={c.key} position={c.position}>
          <TaskClusterMarker
            counts={laneCountsOf(c.tasks)}
            urgency={c.urgency}
            note={c.note}
            fragile={c.fragile}
            held={c.held}
            tier={tier}
            onClick={() => onClickCluster(c.tasks)}
          />
        </AdvancedMarker>
      ))}
    </>
  );
}

// ── trip waypoints (stops) ──────────────────────────────────────────────────
function waypointStatus(tasks: Task[]): "pending" | "active" | "done" {
  if (tasks.length === 0) return "pending";
  if (tasks.every((t) => TERMINAL.includes(t.status))) return "done";
  if (tasks.some((t) => t.status === TaskStatus.IN_PROGRESS)) return "active";
  return "pending";
}

export function TripWaypointMarkers({
  zoomLevel,
  leftInset,
  rightInset,
  topInset,
  onClickStop,
}: {
  zoomLevel: number;
  leftInset: number;
  rightInset: number;
  topInset: number;
  onClickStop: (trip: Trip, stop: DerivedStop) => void;
}) {
  const map = useMap();
  const [camera, setCamera] = useState<{
    center: google.maps.LatLng;
    projection: google.maps.Projection;
    zoom: number;
    width: number;
    height: number;
  } | null>(null);
  const tripFilter = useShortcutsStore((s) => s.tripFilter);
  const scope = useScope();
  const { data: tripsResponse } = useActiveTrips(scope);
  const trips = useMemo(() => tripsResponse?.data ?? [], [tripsResponse]);
  const { data: allWaypoints } = useAllTripWaypoints({ trips });

  const tier = zoomTier(zoomLevel);
  const tripsById = useMemo(
    () => new Map(trips.map((t) => [t.id, t])),
    [trips]
  );

  const visible = useMemo(() => {
    if (!allWaypoints) return [];
    return allWaypoints.filter((wp: Waypoint) => {
      const id = wp.tripId;
      if (!id) return true;
      return tripIsVisible(tripFilter, id);
    });
  }, [allWaypoints, tripFilter]);

  useEffect(() => {
    if (!map) return;
    const refresh = () => {
      const center = map.getCenter();
      const projection = map.getProjection();
      const zoom = map.getZoom();
      if (!center || !projection || zoom == null) return;
      setCamera({
        center,
        projection,
        zoom,
        width: map.getDiv().clientWidth,
        height: map.getDiv().clientHeight,
      });
    };
    const idle = map.addListener("idle", refresh);
    const projection = map.addListener("projection_changed", refresh);
    const resize = new ResizeObserver(refresh);
    resize.observe(map.getDiv());
    refresh();
    return () => {
      idle.remove();
      projection.remove();
      resize.disconnect();
    };
  }, [map]);

  const placementById = useMemo(() => {
    const result = new Map<string, { dx: number; dy: number }>();
    if (!camera || tier === "far") return result;
    const centerPoint = camera.projection.fromLatLngToPoint(camera.center);
    if (!centerPoint) return result;
    const { width, height } = camera;
    if (!width || !height) return result;
    const scale = 2 ** camera.zoom;
    const stops = visible.flatMap((wp) => {
      const coords = getLocationCoordinates(wp.location ?? wp.locationKey);
      if (!coords || !tripsById.has(wp.tripId)) return [];
      const point = camera.projection.fromLatLngToPoint(
        new google.maps.LatLng(coords.lat, coords.lng)
      );
      if (!point) return [];
      let worldX = point.x - centerPoint.x;
      if (worldX > 128) worldX -= 256;
      if (worldX < -128) worldX += 256;
      return [
        {
          id: wp.id,
          x: width / 2 + worldX * scale,
          y: height / 2 + (point.y - centerPoint.y) * scale,
        },
      ];
    });
    const originalById = new Map(stops.map((stop) => [stop.id, stop]));
    for (const placed of spreadOverlappingStops(
      stops,
      { width, height, left: leftInset, right: rightInset, top: topInset },
      tier === "near" ? 30 : 24
    )) {
      if (!placed.displaced) continue;
      const original = originalById.get(placed.id);
      if (original) {
        result.set(placed.id, {
          dx: placed.x - original.x,
          dy: placed.y - original.y,
        });
      }
    }
    return result;
  }, [camera, tier, visible, tripsById, leftInset, rightInset, topInset]);

  return (
    <>
      {visible.map((wp: Waypoint) => {
        const coords = getLocationCoordinates(wp.location ?? wp.locationKey);
        const trip = tripsById.get(wp.tripId);
        if (!coords || !trip) return null;
        const tasks = wp.tasks ?? [];
        const hold = unresolvedHoldState(tasks);
        const status = waypointStatus(tasks);
        const placement = placementById.get(wp.id);
        const marker = (
          <WaypointMarker
            seq={wp.sequence}
            color={trip.color ?? "#6366f1"}
            status={status}
            counts={laneCountsOf(tasks)}
            note={tasks.some((t) => getTaskCareSignals(t).note)}
            fragile={tasks.some((t) => getTaskCareSignals(t).fragile)}
            tier={tier}
            holdCount={hold.heldCount}
            allHeld={hold.allHeld}
            extendedStem={Boolean(placement)}
            onClick={() => onClickStop(trip, buildStops([wp])[0])}
          />
        );
        if (placement) {
          const { dx, dy } = placement;
          const length = Math.hypot(dx, dy);
          const stemColor =
            status === "done" || hold.allHeld ? "#94a3b8" : "#0b0e14";
          return (
            <AdvancedMarker
              key={wp.id}
              position={coords}
              anchorLeft="0px"
              anchorTop="0px"
            >
              <div className="relative h-0 w-0">
                <div
                  className="pointer-events-none absolute"
                  style={{
                    left: 0,
                    top: -1,
                    width: length,
                    height: 2,
                    background: stemColor,
                    transformOrigin: "0 50%",
                    transform: `rotate(${Math.atan2(dy, dx)}rad)`,
                  }}
                />
                <div
                  className="pointer-events-none absolute rounded-full bg-white"
                  style={{
                    left: -3.5,
                    top: -3.5,
                    width: 7,
                    height: 7,
                    border: `1.5px solid ${stemColor}`,
                  }}
                />
                <div
                  className="absolute"
                  style={{
                    left: dx,
                    top: dy,
                    transform: "translate(-50%, -100%)",
                  }}
                >
                  {marker}
                </div>
              </div>
            </AdvancedMarker>
          );
        }
        return (
          <AdvancedMarker key={wp.id} position={coords}>
            {marker}
          </AdvancedMarker>
        );
      })}
    </>
  );
}

// ── operators (live drivers) ────────────────────────────────────────────────
const OP_PALETTE = [
  "#4f46e5",
  "#0891b2",
  "#16a34a",
  "#d97706",
  "#db2777",
  "#7c3aed",
  "#0d9488",
];

function opColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) & 0xffff;
  return OP_PALETTE[h % OP_PALETTE.length];
}

export function OperatorMarkers({
  zoomLevel,
  onClickOperator,
}: {
  zoomLevel: number;
  onClickOperator?: (op: Operator) => void;
}) {
  const showOperators = useShortcutsStore((s) => s.showOperators);
  const scope = useScope();
  const { operators } = useSocketOperators({
    limit: 100,
    status: "ON_DUTY,OFF_DUTY",
    ...scope,
  });

  const tier = zoomTier(zoomLevel);

  // Group operators that share (about) the same point so overlapping drivers
  // render as an avatar-group +N stack instead of stacking on top of each other.
  const groups = new Map<
    string,
    { lat: number; lng: number; ops: Operator[] }
  >();
  for (const o of operators ?? []) {
    if (!o.lastLocation?.lat || !o.lastLocation?.lng) continue;
    const lat = o.lastLocation.lat;
    const lng = o.lastLocation.lng;
    const key = `${lat.toFixed(4)},${lng.toFixed(4)}`;
    const g = groups.get(key) ?? { lat, lng, ops: [] };
    g.ops.push(o);
    groups.set(key, g);
  }

  if (!showOperators || !operators) return null;

  return (
    <>
      {[...groups.entries()].map(([key, g]) =>
        g.ops.length === 1 ? (
          <AdvancedMarker key={key} position={{ lat: g.lat, lng: g.lng }}>
            <OperatorMarker
              initials={getOperatorInitials(g.ops[0])}
              color={opColor(g.ops[0].id)}
              vehicle={Boolean(g.ops[0].vehicle)}
              rolling={
                g.ops[0].status === "ON_DUTY" && Boolean(g.ops[0].isTracking)
              }
              tier={tier}
              onClick={
                onClickOperator ? () => onClickOperator(g.ops[0]) : undefined
              }
            />
          </AdvancedMarker>
        ) : (
          <AdvancedMarker key={key} position={{ lat: g.lat, lng: g.lng }}>
            <StackedOperators
              items={g.ops.map((o) => ({
                initials: getOperatorInitials(o),
                color: opColor(o.id),
              }))}
              onClick={
                onClickOperator ? () => onClickOperator(g.ops[0]) : undefined
              }
            />
          </AdvancedMarker>
        )
      )}
    </>
  );
}

// ── depots ──────────────────────────────────────────────────────────────────
export function DepotMarkers({ zoomLevel }: { zoomLevel: number }) {
  const showDepots = useShortcutsStore((s) => s.showDepots);
  const { carrierIds, dispatchCenterIds } = useCarrierScope();
  const { data: allDispatchCenters } = useGetDispatchCenters();
  const carrierSet = new Set(carrierIds);
  const centerSet = new Set(dispatchCenterIds);
  const dispatchCenters = allDispatchCenters?.filter(
    (center) =>
      carrierSet.has(center.pickupProviderCarrier.id) &&
      (dispatchCenterIds.length === 0 || centerSet.has(center.id))
  );

  const tier = zoomTier(zoomLevel);
  if (!showDepots || !dispatchCenters) return null;

  return (
    <>
      {dispatchCenters.map((center) => {
        const loc = center.address?.location;
        if (!loc?.lat || !loc?.lng) return null;
        return (
          <AdvancedMarker
            key={center.id}
            position={{ lat: loc.lat, lng: loc.lng }}
          >
            <DepotMarker tier={tier} name={center.name} />
          </AdvancedMarker>
        );
      })}
    </>
  );
}
